import { IconChevronRight, IconSearch, IconUserPlus } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Banner, EmptyState, Sheet, Spinner, useToast } from '@/components/common';
import { useResource } from '@/hooks';
import { errorMessage } from '@/lib/api-client';
import { navigate } from '@/lib/router';
import { formatDate } from '../chart/components';
import { patientsApi } from './api';
import { PatientForm } from './PatientForm';

/** How many patients the home screen lists (most recent first). */
const RECENT = 40;

/** Year of birth only, as the list shows it. */
const birthYear = (date: string | null) => (date ? date.slice(0, 4) : null);

/** The home screen's left half: search, the 40 most recent patients and "New patient". */
export function RecentPatients() {
  const { t } = useTranslation();
  const toast = useToast();
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  const patients = useResource(() => patientsApi.list(debounced, RECENT), debounced);

  return (
    <section className="home__patients" aria-label={t('home.patients')}>
      <div className="stack">
        <div className="search">
          <IconSearch size={20} />
          <input
            className="input"
            type="search"
            placeholder={t('patients.searchPlaceholder')}
            aria-label={t('patients.searchLabel')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        {patients.error ? (
          <Banner
            tone="error"
            action={
              <button type="button" className="btn btn--sm" onClick={() => patients.reload()}>
                {t('common.retry')}
              </button>
            }
          >
            {errorMessage(patients.error)}
          </Banner>
        ) : !patients.data ? (
          <Spinner />
        ) : patients.data.length === 0 ? (
          <EmptyState>{debounced ? t('patients.noResults') : t('patients.empty')}</EmptyState>
        ) : (
          <section className="card" style={{ padding: '4px 12px' }}>
            <ul className="list">
              {patients.data.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    className="list-item"
                    onClick={() => navigate({ name: 'patient', id: p.id })}
                  >
                    <span className="avatar" aria-hidden="true">
                      {p.lastName[0]}
                      {p.firstName[0]}
                    </span>
                    <span className="grow">
                      <strong>
                        {p.lastName} {p.firstName}
                      </strong>
                      <span className="small muted" style={{ display: 'block' }}>
                        {[
                          birthYear(p.birthDate),
                          p.lastVisitAt ? `> ${formatDate(p.lastVisitAt)}` : null,
                        ]
                          .filter(Boolean)
                          .join(' · ') || '—'}
                      </span>
                    </span>
                    {p.pendingCount > 0 && (
                      <span className="badge badge--pending">
                        {t('common.waiting', { count: p.pendingCount })}
                      </span>
                    )}
                    <IconChevronRight size={20} className="muted" />
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>

      {/* Under the list, and kept in sight while a long list scrolls. */}
      <div className="home__new">
        <button type="button" className="btn btn--primary" onClick={() => setCreating(true)}>
          <IconUserPlus size={20} /> {t('patients.newTitle')}
        </button>
      </div>

      {creating && (
        <Sheet title={t('patients.newTitle')} onClose={() => setCreating(false)}>
          <PatientForm
            busy={busy}
            onSubmit={async (input) => {
              setBusy(true);
              try {
                const created = await patientsApi.create(input);
                setCreating(false);
                navigate({ name: 'patient', id: created.id });
              } catch (err) {
                toast.show(errorMessage(err), 'error');
              } finally {
                setBusy(false);
              }
            }}
          />
        </Sheet>
      )}
    </section>
  );
}
